import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useGoogleLogin } from '@react-oauth/google';
import { useMemo } from 'react';
import { Loading } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';
import { detectInAppBrowser } from '@/core/utils/browser';

const RETURN_URL_KEY = 'login_return_url';
const CALLBACK_PATH = '/auth/callback';

// Apple Sign-In の設定
const APPLE_CLIENT_ID = 'run.runport';

interface LoginButtonsProps {
  /** ログイン後のリダイレクト先 */
  returnUrl: string;
  /** ボタンを無効化 */
  disabled?: boolean;
}

export function LoginButtons({ returnUrl, disabled }: LoginButtonsProps) {
  const theme = useTheme();
  const { loading: authLoading } = useAuth();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const inAppBrowserInfo = useMemo(() => detectInAppBrowser(), []);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    ux_mode: 'redirect',
    redirect_uri: window.location.origin + CALLBACK_PATH,
  });

  // Google ログインボタン押下時: 戻り先を保存してからリダイレクト
  const handleGoogleLogin = () => {
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    googleLogin();
  };

  // Apple ログインボタン押下時: 戻り先を保存してから Apple にリダイレクト
  const handleAppleLogin = () => {
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    sessionStorage.setItem('apple_client_id', APPLE_CLIENT_ID);

    // Apple コールバック URL（Hosting rewrite で Cloud Function に転送）
    const appleCallbackUrl = `${window.location.origin}/auth/apple-callback`;

    // state に origin を含める（コールバック後のリダイレクト先を特定するため）
    const stateValue = `${crypto.randomUUID()}:${window.location.origin}`;
    sessionStorage.setItem('apple_auth_state', stateValue);

    const params = new URLSearchParams({
      client_id: APPLE_CLIENT_ID,
      redirect_uri: appleCallbackUrl,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope: 'email',
      state: stateValue,
    });

    window.location.href = `https://appleid.apple.com/auth/authorize?${params.toString()}`;
  };

  if (authLoading) {
    return <Loading />;
  }

  // アプリ内ブラウザの場合はガイダンスを表示
  if (inAppBrowserInfo.isInAppBrowser) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            maxWidth: 320,
            textAlign: 'center',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
            borderRadius: '16px',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <OpenInNewIcon
            sx={{
              fontSize: 48,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: 'text.primary',
            }}
          >
            ブラウザを変更してください
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.7,
            }}
          >
            このブラウザではログインできません
            <br />
            SafariやChromeで開き直してください
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
      {/* Apple Sign-In（利用者が多いため上に配置） */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={disabled}
        startIcon={<AppleIcon />}
        onClick={handleAppleLogin}
        sx={{
          background: gradient,
          color: '#fff',
          height: 52,
          maxWidth: 300,
          fontWeight: 600,
          borderRadius: '12px',
          boxShadow: `0 4px 20px ${theme.palette.primary.main}40`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: gradient,
            transform: 'translateY(-3px) scale(1.02)',
            boxShadow: `0 8px 30px ${theme.palette.primary.main}50`,
          },
          '&:active': {
            transform: 'translateY(-1px) scale(0.98)',
          },
        }}
      >
        Appleでログイン
      </Button>

      {/* Google Sign-In */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={disabled}
        startIcon={<GoogleIcon />}
        onClick={handleGoogleLogin}
        sx={{
          background: gradient,
          color: '#fff',
          height: 52,
          maxWidth: 300,
          fontWeight: 600,
          borderRadius: '12px',
          boxShadow: `0 4px 20px ${theme.palette.secondary.main}40`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: gradient,
            transform: 'translateY(-3px) scale(1.02)',
            boxShadow: `0 8px 30px ${theme.palette.secondary.main}50`,
          },
          '&:active': {
            transform: 'translateY(-1px) scale(0.98)',
          },
        }}
      >
        Googleでログイン
      </Button>
    </Box>
  );
}
